import { Component, Input, OnInit } from '@angular/core';
import { ConfirmPaymentAmountResponse } from 'app/payment/payment';
import { PaymentService } from 'app/payment/payment.service';
import { SelectsDataInterface, Statement } from 'app/statements';
import { StatementPaymentAmount } from 'app/statements/statement-payment-amont';
import { StatementPaymentAmountService } from 'app/statements/statement-payment-amount.service';
import { takeUntil } from 'rxjs';
import { SubscribingComponent } from '../subscribing-component';

type PriceChangeInfoType = "premium_price_change" | "premium_plus_downgrade";

@Component({
  selector: 'app-payment-info',
  templateUrl: './payment-info.component.html',
  styleUrls: ['./payment-info.component.scss'],
  standalone: false
})
export class PaymentInfoComponent extends SubscribingComponent implements OnInit {
  @Input() statement: Statement;
  @Input() dataSelects: SelectsDataInterface;
  @Input() showPriceChangeInfo = false;
  public paymentConfirmation: ConfirmPaymentAmountResponse;
  public statementPaymentAmount: StatementPaymentAmount;
  public currencySymbol: string = "";

  constructor(
    private paymentService: PaymentService,
    private statementPaymentAmountService: StatementPaymentAmountService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.statement?.id) {
      return;
    }

    this.currencySymbol = this.dataSelects.packageOptions.currencySymbol.symbol;
    this.paymentService
      .getPaymentAmount(this.statement.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: ConfirmPaymentAmountResponse) => {
          this.paymentConfirmation = response;
        }
      });

    this.statementPaymentAmountService
      .getStatementPaymentAmount(this.statement.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: StatementPaymentAmount) => {
          this.statementPaymentAmount = response;
        }
      });
  }

  public get shouldShowPriceChangeInfo(): boolean {
    if (!this.priceChangeInfoType) {
      return false;
    }

    return !!this.showPriceChangeInfo && !!this.statement?.request_new_price_acceptance;
  }

  public get isPremiumPlusDowngradeInfo(): boolean {
    return this.priceChangeInfoType === "premium_plus_downgrade";
  }

  public get vatPercentFormatted(): string {
    if (this.statementPaymentAmount?.vatPercentFormatted) {
      return this.statementPaymentAmount.vatPercentFormatted;
    }

    const amountNetto = Number(this.paymentConfirmation?.amount_netto || 0);
    const amountVat = Number(this.paymentConfirmation?.amount_vat || 0);

    if (amountNetto <= 0) {
      return "19%";
    }

    return `${Math.round((amountVat / amountNetto) * 100)}%`;
  }

  private get priceChangeInfoType(): PriceChangeInfoType | null {
    const acceptedIsPercentage = this.statement?.acceptedIsPercentagePayment;
    const currentIsPercentagePayment = this.currentIsPercentagePayment;
    if (acceptedIsPercentage === true && currentIsPercentagePayment === false) {
      return "premium_plus_downgrade";
    }

    const explicitType = this.statement?.priceChangeNotificationType;
    if (explicitType) {
      return explicitType;
    }

    if (acceptedIsPercentage === false) {
      return "premium_price_change";
    }

    if (this.statement?.request_new_price_acceptance) {
      return "premium_price_change";
    }

    return null;
  }

  private get currentIsPercentagePayment(): boolean | null {
    if (typeof this.paymentConfirmation?.is_percentage_payment === "boolean") {
      return this.paymentConfirmation.is_percentage_payment;
    }

    if (typeof this.statementPaymentAmount?.isPercentagePayment === "boolean") {
      return this.statementPaymentAmount.isPercentagePayment;
    }

    return null;
  }
}
